import { useEffect, useState } from "react";
import { Code, Disc as Discord, Globe, SquarePlay } from "lucide-react";

import { IBoxLogo, StatusPill } from "./ui";

const sections = [
  {
    title: "PRODUCT",
    links: [
      { label: "Apps", href: "/#apps" },
      { label: "Pricing", href: "/#pricing" },
      { label: "Download", href: "/download" },
      { label: "Documentation", href: "/docs/" },
      { label: "Tutorials", href: "/docs/quick-start/browser-source" },
      { label: "Support", href: "/docs/troubleshooting" },
    ],
  },
  {
    title: "COMPANY",
    links: [
      { label: "About", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Terms", href: "/terms" },
      { label: "Privacy", href: "/privacy" },
      { label: "Cookies", href: "/cookies" },
      { label: "License", href: "/license" },
    ],
  },
];

const socialIcons = [Globe, Discord, SquarePlay, Code];
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
type ApiStatus = "checking" | "online" | "offline";

async function checkApiHealth() {
  const response = await fetch(`${apiBaseUrl.replace(/\/$/, "")}/health`, {
    cache: "no-store",
  });
  if (!response.ok) throw new Error("API health check failed.");
  const body = (await response.json().catch(() => null)) as { ok?: boolean } | null;
  if (!body?.ok) throw new Error("API health check failed.");
}

export function Footer() {
  const [apiStatus, setApiStatus] = useState<ApiStatus>("checking");
  const [statusMessage, setStatusMessage] = useState("");

  async function refreshApiStatus(showMessage = false) {
    setStatusMessage("");
    try {
      await checkApiHealth();
      setApiStatus("online");
      if (showMessage) setStatusMessage("API health check passed.");
    } catch {
      setApiStatus("offline");
      setStatusMessage("API health check failed. iBox services may be unavailable.");
    }
  }

  useEffect(() => {
    void refreshApiStatus();
  }, []);

  const statusLabel =
    apiStatus === "online" ? "All Systems Operational" : apiStatus === "checking" ? "Checking Systems" : "API Unavailable";
  const statusKind = apiStatus === "online" ? "success" : apiStatus === "checking" ? "idle" : "warning";

  return (
    <footer className="border-t border-border bg-bg pb-8 pt-16">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 grid grid-cols-1 gap-10 md:grid-cols-6">
          <div className="md:col-span-3">
            <IBoxLogo className="mb-5 h-8" />
            <p className="mb-6 max-w-xs text-sm text-muted">The professional broadcast toolkit for iRacing streamers.</p>
            <div className="mb-6 hidden gap-2">
              {socialIcons.map((Icon) => (
                <a
                  key={Icon.name}
                  href="#"
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-surface text-muted transition-colors hover:border-primary/50 hover:text-primary"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
            <div className="space-y-1 font-mono text-[10px] tracking-[0.2em] text-muted">
              <div>SYS // 2.4.0</div>
              <div>BUILD // 2026.05</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-10 md:col-span-2 md:col-start-5">
            {sections.map((section) => (
              <div key={section.title}>
                <h4 className="mb-4 text-[11px] font-semibold tracking-[0.25em] text-text">{section.title}</h4>
                <ul className="space-y-2.5">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="text-sm text-muted transition-colors hover:text-text">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-border pt-6 md:flex-row">
          <div className="text-xs text-muted">© 2026 iBox App. All rights reserved.</div>
          <div className="flex flex-col items-center gap-2 md:items-end">
            <button type="button" onClick={() => void refreshApiStatus(true)} className="cursor-pointer">
              <StatusPill status={statusKind} label={statusLabel} pulse={apiStatus === "online"} />
            </button>
            {statusMessage ? <div className="text-xs text-muted">{statusMessage}</div> : null}
          </div>
        </div>
      </div>
    </footer>
  );
}
